import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/api"
import { Button } from "@/components/ui/Button"
import {
  Mail, RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle,
  Play, Settings, Loader2, Linkedin, ArrowRight,
} from "lucide-react"

interface PollStatus {
  configured: boolean
  imap_host: string | null
  imap_username: string | null
  poll_interval_minutes: number
  last_poll_at: string | null
  last_error: string | null
  is_healthy: boolean | null
  consecutive_errors: number
  total_events_loaded: number
  total_emails_scanned: number
  last_uid: number | null
  never_polled: boolean
}

function formatRelative(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const diff = Math.floor((now.getTime() - d.getTime()) / 1000)
  if (diff < 60) return "agora mesmo"
  if (diff < 3600) return `${Math.floor(diff / 60)} min atrás`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
}

export default function OutreachPollBanner() {
  const qc = useQueryClient()
  const [showInterval, setShowInterval] = useState(false)
  const [interval, setInterval] = useState(5)

  const { data: status, isLoading } = useQuery<PollStatus>({
    queryKey: ["outreach-poll-status"],
    queryFn: async () => {
      const { data } = await api.get("/v1/outreach/poll-status")
      return data
    },
    refetchInterval: 30000,
  })

  const pollNow = useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/v1/outreach/poll-now")
      return data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["outreach-poll-status"] })
      qc.invalidateQueries({ queryKey: ["outreach"] })
      alert(data.message || "Leitura concluída")
    },
    onError: (err: any) => {
      alert(err?.response?.data?.detail || "Erro ao ler emails")
    },
  })

  const updateInterval = useMutation({
    mutationFn: async (minutes: number) => {
      const { data } = await api.put("/v1/outreach/config/poll-interval", { minutes })
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["outreach-poll-status"] })
      setShowInterval(false)
    },
  })

  if (isLoading) return null

  // Not configured — show setup prompt
  if (!status?.configured) {
    return (
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">Monitoramento LinkedIn não configurado</p>
          <p className="text-xs text-amber-600 dark:text-amber-300 mt-1">
            Configure sua conta de email em Configurações &gt; LinkedIn Outreach para monitorar automaticamente convites aceitos, mensagens e InMails do LinkedIn.
          </p>
        </div>
        <a href="/settings" className="flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-300 hover:underline flex-shrink-0">
          <Settings className="h-3.5 w-3.5" /> Configurar <ArrowRight className="h-3 w-3" />
        </a>
      </div>
    )
  }

  // First time — never polled
  if (status.never_polled) {
    return (
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
        <Linkedin className="h-5 w-5 text-[#0A66C2] flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">Conta configurada — pronto para iniciar</p>
          <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
            Monitorando <strong>{status.imap_username}</strong> via {status.imap_host}. Clique em "Ler Agora" para fazer a carga inicial dos emails do LinkedIn.
          </p>
        </div>
        <Button
          onClick={() => pollNow.mutate()}
          disabled={pollNow.isPending}
          className="text-xs flex-shrink-0"
        >
          {pollNow.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />}
          {pollNow.isPending ? "Lendo emails..." : "Ler Agora"}
        </Button>
      </div>
    )
  }

  // Active — show status
  const isHealthy = status.is_healthy !== false
  const hasError = !!status.last_error

  return (
    <div className={`border rounded-lg p-3 flex items-center gap-3 flex-wrap ${isHealthy ? "bg-card border-border" : "bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800"}`}>
      {/* Status icon */}
      {isHealthy ? (
        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
      ) : (
        <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
      )}

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-foreground">
            <Mail className="inline h-3 w-3 mr-1 opacity-50" />
            {status.imap_username}
          </span>
          <span className="text-[10px] text-muted-foreground">•</span>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Última leitura: {status.last_poll_at ? formatRelative(status.last_poll_at) : "nunca"}
          </span>
          <span className="text-[10px] text-muted-foreground">•</span>
          <span className="text-xs text-muted-foreground">
            {status.total_events_loaded} evento(s) carregado(s)
          </span>
          <span className="text-[10px] text-muted-foreground">•</span>
          <span className="text-xs text-muted-foreground">
            Cadência: a cada {status.poll_interval_minutes} min
            <button onClick={() => { setInterval(status.poll_interval_minutes); setShowInterval(!showInterval) }} className="ml-1 text-primary hover:underline text-[10px]">(alterar)</button>
          </span>
        </div>
        {hasError && (
          <p className="text-[10px] text-red-500 mt-1 truncate">Erro: {status.last_error}</p>
        )}
      </div>

      {/* Interval editor */}
      {showInterval && (
        <div className="flex items-center gap-2 bg-muted rounded-md px-2 py-1">
          <select
            value={interval}
            onChange={e => setInterval(Number(e.target.value))}
            className="text-xs bg-background border rounded px-2 py-1"
          >
            <option value={1}>1 min</option>
            <option value={2}>2 min</option>
            <option value={5}>5 min</option>
            <option value={10}>10 min</option>
            <option value={15}>15 min</option>
            <option value={30}>30 min</option>
            <option value={60}>1 hora</option>
          </select>
          <Button size="sm" onClick={() => updateInterval.mutate(interval)} disabled={updateInterval.isPending} className="text-[10px] h-6 px-2">
            Salvar
          </Button>
        </div>
      )}

      {/* Actions */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => pollNow.mutate()}
        disabled={pollNow.isPending}
        className="text-xs h-7 flex-shrink-0"
      >
        {pollNow.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
        {pollNow.isPending ? "Lendo..." : "Ler Agora"}
      </Button>
    </div>
  )
}
